import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { GraduationCapIcon, PlusIcon, SearchIcon, FilterIcon, ChevronDownIcon, EditIcon, TrashIcon, EyeIcon, ArrowRightIcon, GlobeIcon, BookOpenIcon, CalendarIcon } from 'lucide-react';
const EliteTrack: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [fieldFilter, setFieldFilter] = useState('All Fields');
  // Sample Elite Track scholarships data
  const eliteScholarships = [{
    id: 'elite001',
    title: 'Computer Science at MIT',
    university: 'Massachusetts Institute of Technology',
    field: 'Computer Science',
    location: 'United States',
    status: 'Active',
    deadline: '2023-12-15',
    applicants: 25,
    acceptedStudents: 2,
    budget: '$500,000',
    description: 'Full scholarship for outstanding Emirati students to pursue Computer Science at MIT'
  }, {
    id: 'elite002',
    title: 'Medicine at Harvard',
    university: 'Harvard University',
    field: 'Medicine',
    location: 'United States',
    status: 'Active',
    deadline: '2023-10-30',
    applicants: 18,
    acceptedStudents: 1,
    budget: '$600,000',
    description: 'Prestigious scholarship for medical studies at Harvard University'
  }, {
    id: 'elite003',
    title: 'Business at London Business School',
    university: 'London Business School',
    field: 'Business',
    location: 'United Kingdom',
    status: 'Active',
    deadline: '2023-11-30',
    applicants: 32,
    acceptedStudents: 3,
    budget: '$400,000',
    description: 'Full scholarship for Emirati students to pursue an MBA at LBS'
  }, {
    id: 'elite004',
    title: 'Engineering at Stanford',
    university: 'Stanford University',
    field: 'Engineering',
    location: 'United States',
    status: 'Draft',
    deadline: '2024-01-15',
    applicants: 0,
    acceptedStudents: 0,
    budget: '$550,000',
    description: 'Elite scholarship for engineering studies at Stanford University'
  }];
  const statusOptions = ['All', 'Active', 'Draft', 'Closed'];
  const fieldOptions = ['All Fields', 'Computer Science', 'Medicine', 'Business', 'Engineering'];
  // Filter scholarships
  const filteredScholarships = eliteScholarships.filter(scholarship => {
    const matchesSearch = searchQuery === '' || scholarship.title.toLowerCase().includes(searchQuery.toLowerCase()) || scholarship.university.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All' || scholarship.status === statusFilter;
    const matchesField = fieldFilter === 'All Fields' || scholarship.field === fieldFilter;
    return matchesSearch && matchesStatus && matchesField;
  });
  const getStatusColor = status => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Closed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const formatDate = dateString => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  return <Layout title="Elite Track Scholarships" persona="MoHESR Admin" data-id="element-2853">
      <div className="max-w-7xl mx-auto" data-id="element-2854">
        {/* Header with stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-2855">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2856">
            <div className="flex items-start justify-between" data-id="element-2857">
              <div data-id="element-2858">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2859">
                  Total Elite Scholarships
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-2860">15</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2861">4 active programs</p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-2862">
                <GraduationCapIcon className="w-6 h-6 text-primary-500" data-id="element-2863" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2864">
            <div className="flex items-start justify-between" data-id="element-2865">
              <div data-id="element-2866">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2867">
                  Total Applications
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-2868">75</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2869">15 this month</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-full" data-id="element-2870">
                <BookOpenIcon className="w-6 h-6 text-blue-500" data-id="element-2871" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2872">
            <div className="flex items-start justify-between" data-id="element-2873">
              <div data-id="element-2874">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2875">
                  Accepted Students
                </h3>
                <div className="text-3xl font-bold text-green-500" data-id="element-2876">6</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2877">8% acceptance rate</p>
              </div>
              <div className="p-3 bg-green-50 rounded-full" data-id="element-2878">
                <GlobeIcon className="w-6 h-6 text-green-500" data-id="element-2879" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2880">
            <div className="flex items-start justify-between" data-id="element-2881">
              <div data-id="element-2882">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2883">Total Budget</h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-2884">
                  $2.05M
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2885">This academic year</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-full" data-id="element-2886">
                <CalendarIcon className="w-6 h-6 text-yellow-500" data-id="element-2887" />
              </div>
            </div>
          </div>
        </div>
        {/* Actions and Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-2888">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4" data-id="element-2889">
            <div className="relative flex-grow max-w-md" data-id="element-2890">
              <input type="text" placeholder="Search scholarships..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-2891" />
              <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" data-id="element-2892" />
            </div>
            <button className="px-4 py-2 bg-primary-500 text-white rounded-md font-medium hover:bg-primary-600 transition-colors flex items-center" data-id="element-2893">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-2894" />
              Create Elite Scholarship
            </button>
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-2895">
            <div className="relative" data-id="element-2896">
              <select className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none pr-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-2897">
                {statusOptions.map(status => <option key={status} value={status} data-id="element-2898">
                    Status: {status}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-2899" />
            </div>
            <div className="relative" data-id="element-2900">
              <select className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none pr-8" value={fieldFilter} onChange={e => setFieldFilter(e.target.value)} data-id="element-2901">
                {fieldOptions.map(field => <option key={field} value={field} data-id="element-2902">
                    {field}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-2903" />
            </div>
          </div>
        </div>
        {/* Scholarships Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2904">
          <div className="overflow-x-auto" data-id="element-2905">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2906">
              <thead className="bg-gray-50" data-id="element-2907">
                <tr data-id="element-2908">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2909">
                    Scholarship
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2910">
                    University
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2911">
                    Field
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2912">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2913">
                    Applications
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2914">
                    Deadline
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2915">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2916">
                {filteredScholarships.map(scholarship => <tr key={scholarship.id} className="hover:bg-gray-50" data-id="element-2917">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2918">
                      <div data-id="element-2919">
                        <div className="text-sm font-medium text-gray-900" data-id="element-2920">
                          {scholarship.title}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-2921">
                          {scholarship.location}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2922">
                      <div className="text-sm text-gray-900" data-id="element-2923">
                        {scholarship.university}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2924">
                      <div className="text-sm text-gray-900" data-id="element-2925">
                        {scholarship.field}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2926">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(scholarship.status)}`} data-id="element-2927">
                        {scholarship.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2928">
                      <div className="text-sm text-gray-900" data-id="element-2929">
                        {scholarship.applicants}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-2930">
                        {scholarship.acceptedStudents} accepted
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2931">
                      {formatDate(scholarship.deadline)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-2932">
                      <div className="flex space-x-2" data-id="element-2933">
                        <button className="text-primary-500 hover:text-primary-700" title="View Details" data-id="element-2934">
                          <EyeIcon className="w-4 h-4" data-id="element-2935" />
                        </button>
                        <button className="text-blue-500 hover:text-blue-700" title="Edit" data-id="element-2936">
                          <EditIcon className="w-4 h-4" data-id="element-2937" />
                        </button>
                        {scholarship.status === 'Draft' && <button className="text-red-500 hover:text-red-700" title="Delete" data-id="element-2938">
                            <TrashIcon className="w-4 h-4" data-id="element-2939" />
                          </button>}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>;
};
export default EliteTrack;